import { useState } from "react";
import { IsOrangeLight, IsRightToLeftTheme, themeC } from "../theme/themehelper";
import { Grid, Skeleton, Typography } from "@mui/material";
import useDetailedFixture from "../fixtures/useDetailedFixture";
import { useTranslation } from "react-i18next";
import MatchInformation from "./matchinformation";
import MatchStatistics from "./matchstatistics";
import LeagueTable from "./leaguetable";
import { Fixture } from "../models/fixture";

export const SlimCouponStyles = {

    rowStyle: {
        flexDirection: "var(--flex-direction)",
        justifyContent: "space-between",
        alignItems: "center"
    },

    infoTextStyle: {
        fontSize: "12px",
        lineHeight: "19px",
        fontWeight: "400",
        fontFamily: "var(--font-body)",
        marginTop: "2px",
        padding: "3px",
        color: "var(--text-black)"
    },

    teamStyle: {
        fontFamily: "var(--font-body)",
        fontSize: "13px",
        lineHeight: "19px",
        fontWeight: "500",
        marginTop: "10px",
        marginBottom: "10px"
    }
}

let oddBackground = "var(--background_1)";
let evenBackground = "var(--background_2)";

if (window.theme === themeC.Dbet) {
    oddBackground = "var(--background_2)";
    evenBackground = "#DBDBDB";
}

let rowPadding = "3px";
let rowColorOdd = "var(--text-black-dark-background)";
let rowColorEven = "var(--text-black)";
if (window.theme === themeC.GoGo || window.theme === themeC.Wood) {
    rowPadding = "5px 8px";
    rowColorOdd = "#B3B3B3";
    rowColorEven = "#B3B3B3";
}

if (window.theme === themeC.Orange) {
    rowPadding = "5px 8px";
    rowColorOdd = IsOrangeLight(window.theme) ? "#636363" : "#B3B3B3";
    rowColorEven = rowColorOdd;
}

if (window.theme === themeC.Green) {    
    oddBackground = "#EEEEEE";
}

export const SlimCouponRowStyles = {
    rowOdd: { ...SlimCouponStyles.rowStyle, ...{ background: oddBackground }, ...SlimCouponStyles.infoTextStyle, ...{ color: rowColorOdd, padding: rowPadding } },
    rowEven: { ...SlimCouponStyles.rowStyle, ...{ background: evenBackground }, ...SlimCouponStyles.infoTextStyle, ...{ color: rowColorEven, padding: rowPadding } }
}

function SlimFixtureDetail({ theme, fixture, isHockey }: { theme: string, fixture: Fixture, isHockey: boolean }) {

    let [menuIndex, setMenuIndex] = useState(0);
    let isRightToLeft = IsRightToLeftTheme(theme);
    const { t } = useTranslation();

    const { fixtureDetails } = useDetailedFixture(fixture.id);

    if (!fixtureDetails) {
        return (
            <Grid container sx={{ margin: "0px", padding: "20px" }} justifyContent="center">
                <Skeleton variant="rectangular" width="100%" height="200px" component="table" />
            </Grid>

        );
    }

    const handleMenuClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, index: number) => {
        setMenuIndex(index);
    };

    let ComponentToRender;

    switch (menuIndex) {
        case 0:
            ComponentToRender = MatchInformation;
            break;
        case 1:
            ComponentToRender = MatchStatistics;
            break;
        case 2:
            ComponentToRender = LeagueTable;
            break;
        default:
            ComponentToRender = MatchInformation;
            break;
    }

    const menu = {
        fontFamily: "var(--font-body)",
        marginTop: "2px",
        cursor: "pointer",
        marginLeft: "5px",
        marginRight: "5px",
        fontSize: "13px",
        lineHeight: "19px",
        fontWeight: "500"
    }

    const underlined = { ...menu, ...{ textDecoration: "underline" } }

    if (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange) {
        const isOrangeLight = IsOrangeLight(theme);
        const activeTabBorder = theme === themeC.GoGo ? '3px solid #F0F0F0' : theme === themeC.Orange ? (isOrangeLight ? '2px solid #1F1F1F' : '2px solid #FE8000') : '3px solid #B19661';
        const tabFont = theme === themeC.GoGo ? "'Poppins'" : theme === themeC.Orange ? "'Hind'" : "'GT-America'";
        const tabBg = isOrangeLight ? '#FFFFFF' : '#121212';
        const tabBorderBottom = isOrangeLight ? '1px solid #DDDDDD' : '1px solid #252525';
        const activeColor = isOrangeLight ? '#1F1F1F' : '#F0F0F0';
        const inactiveColor = isOrangeLight ? '#636363' : '#B3B3B3';
        const activeWeight = theme === themeC.Orange ? 500 : 400;
        const gogoTabs = [
            { label: t('game_info'), idx: 0 },
            ...(!isHockey ? [{ label: t('statistics'), idx: 1 }, { label: t('table'), idx: 2 }] : [])
        ];
        return (
            <Grid>
                <div style={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0px 12px',
                    gap: '4px',
                    height: '36px',
                    overflowX: 'auto',
                    background: tabBg,
                    borderBottom: tabBorderBottom,
                    borderRadius: '0px',
                }}>
                    {gogoTabs.map(({ label, idx }) => (
                        <div
                            key={idx}
                            style={{
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '8px',
                                gap: '4px',
                                height: '36px',
                                flexGrow: 1,
                                borderBottom: menuIndex === idx ? activeTabBorder : '3px solid transparent',
                                cursor: 'pointer',
                            }}
                            onClick={(e) => handleMenuClick(e as any, idx)}
                        >
                            <span style={{
                                fontFamily: tabFont,
                                fontStyle: 'normal',
                                fontWeight: menuIndex === idx ? activeWeight : 400,
                                fontSize: '12px',
                                lineHeight: '20px',
                                color: menuIndex === idx ? activeColor : inactiveColor,
                                whiteSpace: 'nowrap',
                                textTransform: 'uppercase',
                            }}>
                                {label}
                            </span>
                        </div>
                    ))}
                </div>
                <Grid>
                    <ComponentToRender fixture={fixture} fixtureDetails={fixtureDetails} isRightToLeft={isRightToLeft} isHockey={isHockey} />
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid>
            <Grid>
                <Grid sx={{ marginBottom: "20px" }} container justifyContent="center">
                    <Typography sx={menuIndex === 0 ? underlined : menu} onClick={(event) => handleMenuClick(event, 0)}>
                        {t('game_info')}
                    </Typography>
                    {!isHockey &&
                        <Typography sx={menuIndex === 1 ? underlined : menu} onClick={(event) => handleMenuClick(event, 1)}>
                            {t('statistics')}
                        </Typography>
                    }
                    {!isHockey &&
                        <Typography sx={menuIndex === 2 ? underlined : menu} onClick={(event) => handleMenuClick(event, 2)}>
                            {t('table')}
                        </Typography>
                    }
                </Grid>
            </Grid>
            <Grid>
                <ComponentToRender fixture={fixture} fixtureDetails={fixtureDetails} isRightToLeft={isRightToLeft} isHockey={isHockey} />
            </Grid>
        </Grid>
    );

}

export default SlimFixtureDetail;
